import { defineStore } from 'pinia';
import documentTypesApi from '@/api/documentTypesApi.js';

export const useDocumentTypesStore = defineStore('documentTypesStore', {
    state: () => ({
        documentTypes: [],
        fileTypes: [],
        combinedTree: [],
        loading: {
            documentTypes: false,
            fileTypes: false,
        },
    }),

    getters: {
        // Получить тип документа по ID
        getDocumentTypeById: (state) => (id) => {
            return state.documentTypes.find(d => d.id === id);
        },

        // Получить тип файла по ID
        getFileTypeById: (state) => (id) => {
            return state.fileTypes.find(f => f.id === id);
        },
    },

    actions: {
        // === Document Types ===
        async loadDocumentTypes() {
            this.loading.documentTypes = true;
            try {
                const data = await documentTypesApi.getDocumentTypes();

                // Фильтруем только типы документов (for_document_kind === true)
                const rawData = Array.isArray(data) ? data : (data?.results || []);
                this.documentTypes = rawData.filter(item => item.for_document_kind === true);

            } catch (error) {
                console.error('Ошибка загрузки типов документов:', error);
                this.documentTypes = [];
            } finally {
                this.loading.documentTypes = false;
            }
        },

        async loadDocumentType(id) {
            try {
                const data = await documentTypesApi.getDocumentType(id);
                const index = this.documentTypes.findIndex(d => d.id === id);
                if (index !== -1) {
                    this.documentTypes[index] = data;
                } else {
                    this.documentTypes.push(data);
                }
                return data;
            } catch (error) {
                console.error(`Ошибка загрузки типа документа ${id}:`, error);
                throw error;
            }
        },

        async createDocumentType(documentTypeData) {
            try {
                const newDocType = await documentTypesApi.createDocumentType(documentTypeData);

                this.documentTypes.push(newDocType);

                this._buildCombinedTree();

                return newDocType;
            } catch (error) {
                console.error('Ошибка создания типа документа:', error);
                throw error;
            }
        },

        async updateDocumentType(id, documentTypeData) {
            try {
                const updated = await documentTypesApi.updateDocumentType(id, documentTypeData);
                const index = this.documentTypes.findIndex(d => d.id === id);
                if (index !== -1) {
                    this.documentTypes[index] = updated;
                }
                this._buildCombinedTree();
                return updated;
            } catch (error) {
                console.error(`Ошибка обновления типа документа ${id}:`, error);
                throw error;
            }
        },

        async deleteDocumentType(id) {
            try {
                await documentTypesApi.deleteDocumentType(id);
                this.documentTypes = this.documentTypes.filter(d => d.id !== id);
                this._buildCombinedTree();
            } catch (error) {
                console.error(`Ошибка удаления типа документа ${id}:`, error);
                throw error;
            }
        },

        // === File Types ===
        async loadFileTypes() {
            this.loading.fileTypes = true;
            try {
                const data = await documentTypesApi.getFileTypes();

                // Фильтруем только типы файлов (for_file_kind === true)
                const rawData = Array.isArray(data) ? data : (data?.results || []);
                this.fileTypes = rawData.filter(item => item.for_file_kind === true);

            } catch (error) {
                console.error('Ошибка загрузки типов файлов:', error);
                this.fileTypes = [];
            } finally {
                this.loading.fileTypes = false;
            }
        },

        async loadFileType(id) {
            try {
                const data = await documentTypesApi.getFileType(id);
                const index = this.fileTypes.findIndex(f => f.id === id);
                if (index !== -1) {
                    this.fileTypes[index] = data;
                } else {
                    this.fileTypes.push(data);
                }
                return data;
            } catch (error) {
                console.error(`Ошибка загрузки типа файла ${id}:`, error);
                throw error;
            }
        },

        async createFileType(fileTypeData) {
            try {
                const newFileType = await documentTypesApi.createFileType(fileTypeData);

                this.fileTypes.push(newFileType);

                this._buildCombinedTree();

                return newFileType;
            } catch (error) {
                console.error('Ошибка создания типа файла:', error);
                throw error;
            }
        },

        async updateFileType(id, fileTypeData) {
            try {
                const updated = await documentTypesApi.updateFileType(id, fileTypeData);
                const index = this.fileTypes.findIndex(f => f.id === id);
                if (index !== -1) {
                    this.fileTypes[index] = updated;
                }
                this._buildCombinedTree();
                return updated;
            } catch (error) {
                console.error(`Ошибка обновления типа файла ${id}:`, error);
                throw error;
            }
        },

        async deleteFileType(id) {
            try {
                await documentTypesApi.deleteFileType(id);
                this.fileTypes = this.fileTypes.filter(f => f.id !== id);
                this._buildCombinedTree();
            } catch (error) {
                console.error(`Ошибка удаления типа файла ${id}:`, error);
                throw error;
            }
        },

        // === Загрузка всех данных ===
        async loadAll() {
            await Promise.all([
                this.loadDocumentTypes(),
                this.loadFileTypes()
            ]);
            this._buildCombinedTree();
        },

        // === Permissions Management ===
        async updateItemTypePermissions(entityType, id, role_ids, entityFlags = null) {
            try {

                // Формируем payload с role_ids и флагами сущностей (если переданы)
                const payload = { role_ids };
                if (entityFlags) {
                    if (entityFlags.for_work !== undefined) payload.for_work = entityFlags.for_work;
                    if (entityFlags.for_contract !== undefined) payload.for_contract = entityFlags.for_contract;
                    if (entityFlags.for_object !== undefined) payload.for_object = entityFlags.for_object;
                }


                const data = entityType === 'document'
                    ? await documentTypesApi.updateDocumentType(id, payload)
                    : await documentTypesApi.updateFileType(id, payload);


                // Обновляем данные в локальном стейте
                const collection = entityType === 'document' ? this.documentTypes : this.fileTypes;
                const index = collection.findIndex(item => item.id === id);
                if (index !== -1) {
                    collection[index].role_ids = role_ids;
                    if (entityFlags) {
                        if (entityFlags.for_work !== undefined) collection[index].for_work = entityFlags.for_work;
                        if (entityFlags.for_contract !== undefined) collection[index].for_contract = entityFlags.for_contract;
                        if (entityFlags.for_object !== undefined) collection[index].for_object = entityFlags.for_object;
                    }
                }
                this._buildCombinedTree();

                return data;
            } catch (error) {
                console.error(`Ошибка обновления прав для ${entityType} типа ${id}:`, error);
                throw error;
            }
        },

        // === Построение единого дерева ===
        _buildCombinedTree() {

            // Создаем виртуальный корень для файлов
            const rootFiles = {
                id: 'root-files',
                name: 'Файлы',
                item_name: 'Файлы',
                entity_type: 'root',
                children: []
            };

            // Добавляем все типы файлов как дочерние элементы корня "Файлы"
            // Backend использует item_name, добавляем name как алиас
            rootFiles.children = this.fileTypes.map(fileType => ({
                ...fileType,
                name: fileType.item_name || fileType.name,
                entity_type: 'file',
                children: []
            }));

            // Создаем виртуальный корень для документов
            const rootDocs = {
                id: 'root-docs',
                name: 'Документы',
                item_name: 'Документы',
                entity_type: 'root',
                children: []
            };

            // Строим иерархию типов документов (по parent)
            const docMap = {};
            const docRoots = [];

            // Создаём мапу всех типов документов
            // Backend использует item_name, добавляем name как алиас
            this.documentTypes.forEach(docType => {
                docMap[docType.id] = {
                    ...docType,
                    name: docType.item_name || docType.name,
                    entity_type: 'document',
                    children: []
                };
            });

            // Строим дерево типов документов
            this.documentTypes.forEach(docType => {
                if (docType.parent) {
                    const parent = docMap[docType.parent];
                    if (parent) {
                        parent.children.push(docMap[docType.id]);
                    } else {
                        // Если родитель не найден, добавляем в корень
                        docRoots.push(docMap[docType.id]);
                    }
                } else {
                    // Без parent - добавляем в корень
                    docRoots.push(docMap[docType.id]);
                }
            });

            // Добавляем все типы документов как дочерние элементы корня "Документы"
            rootDocs.children = docRoots;

            // Собираем единое дерево
            this.combinedTree = [rootFiles, rootDocs];

        },
    },
});
